// eslint-disable-next-line import/no-cycle
import travelBuddyService from '../../services/travelBuddyService';
// eslint-disable-next-line import/no-cycle
import usersService from '../../services/usersService';
import { getFeatureDisplayNameFromMap, normalizeFeatureLocale } from '@/utils/featureDisplay';

function featureLabelFromRootState(rootState, catalogKey, fallback) {
  const locale = normalizeFeatureLocale(rootState?.auth?.lang || 'en');
  return getFeatureDisplayNameFromMap(
    rootState?.auth?.featureDisplayByKey,
    catalogKey,
    locale,
  ) || fallback;
}

export default {
  namespaced: true,

  state: {
    userHistory: null,
    auditLogs: [],
    auditLogsTotal: 0,
    auditLogsPage: 1,
    auditLogsLimit: 20,
    auditLogsTotalPages: 0,
    loading: false,
    error: null,
    users: [],
    usersLoading: false,
    anomalyScan: null,
    anomalyScanLoading: false,
  },

  mutations: {
    SET_USER_HISTORY(state, payload) {
      state.userHistory = payload;
    },

    SET_AUDIT_LOGS_RESULT(state, {
      data, total, page, limit, totalPages,
    }) {
      state.auditLogs = data || [];
      state.auditLogsTotal = total ?? 0;
      state.auditLogsPage = page ?? 1;
      state.auditLogsLimit = limit ?? 20;
      state.auditLogsTotalPages = totalPages ?? 0;
    },

    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    SET_USERS(state, users) {
      state.users = users || [];
    },

    SET_USERS_LOADING(state, isLoading) {
      state.usersLoading = isLoading;
    },

    SET_ANOMALY_SCAN(state, payload) {
      state.anomalyScan = payload;
    },

    SET_ANOMALY_SCAN_LOADING(state, isLoading) {
      state.anomalyScanLoading = isLoading;
    },
  },

  actions: {
    async fetchUserHistory({ commit }, userEmail) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        commit('SET_USER_HISTORY', null);

        const response = await travelBuddyService.getHistoryByEmail(userEmail);
        const data = response.data || response;
        commit('SET_USER_HISTORY', data);
        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        commit('SET_USER_HISTORY', null);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchAuditLogs({ commit }, { filters = {}, page = 1, limit = 20 } = {}) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const params = { page, limit, ...filters };
        if (params.startDate instanceof Date) params.startDate = params.startDate.toISOString();
        if (params.endDate instanceof Date) params.endDate = params.endDate.toISOString();

        const response = await travelBuddyService.getAuditLogs(params);
        const result = response.data || response;
        commit('SET_AUDIT_LOGS_RESULT', {
          data: result.data,
          total: result.total,
          page: result.page,
          limit: result.limit,
          totalPages: result.totalPages,
        });
        return result;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async exportUserHistory({ commit, dispatch, rootState }, params = {}) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await travelBuddyService.exportHistory(params);
        const blob = response.data || response;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `travel-buddy-history-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        const featureName = featureLabelFromRootState(
          rootState,
          'travelBuddyHistory',
          this.$t('FallbackTexts.travelBuddyHistory'),
        );
        dispatch(
          'notifications/showToast',
          {
            severity: 'success',
            summary: 'Success',
            detail: `${featureName} exported successfully`,
            life: 3000,
          },
          { root: true },
        );
      } catch (error) {
        commit('SET_ERROR', error.message);
        dispatch(
          'notifications/showToast',
          {
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Export failed',
            life: 5000,
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async exportAuditLogs({ commit, dispatch }, filters = {}) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const params = { ...filters };
        if (params.startDate instanceof Date) params.startDate = params.startDate.toISOString();
        if (params.endDate instanceof Date) params.endDate = params.endDate.toISOString();

        if (
          params.startDate == null
          || params.startDate === ''
          || params.endDate == null
          || params.endDate === ''
        ) {
          throw new Error('Start date and end date are required to export the audit log.');
        }
        const start = new Date(params.startDate);
        const end = new Date(params.endDate);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          throw new Error('Invalid start or end date for export.');
        }
        if (start.getTime() > end.getTime()) {
          throw new Error('Start date must be before or equal to end date.');
        }
        const exportMaxSpanMs = 366 * 24 * 60 * 60 * 1000;
        if (end.getTime() - start.getTime() > exportMaxSpanMs) {
          throw new Error('Date range cannot exceed 366 days.');
        }

        const response = await travelBuddyService.exportAuditLogs(params);
        const blob = response.data || response;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `travel-buddy-audit-log-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        dispatch(
          'notifications/showToast',
          {
            severity: 'success',
            summary: 'Success',
            detail: 'Audit log exported successfully',
            life: 3000,
          },
          { root: true },
        );
      } catch (error) {
        commit('SET_ERROR', error.message);
        dispatch(
          'notifications/showToast',
          {
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to export audit log',
            life: 5000,
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async triggerMatching({ commit, dispatch }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await travelBuddyService.triggerMatching();
        const data = response.data || response;

        dispatch(
          'notifications/showToast',
          {
            severity: 'success',
            summary: 'Matching Triggered',
            detail: 'Travel buddy matching has been triggered successfully',
            life: 3000,
          },
          { root: true },
        );

        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        dispatch(
          'notifications/showToast',
          {
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to trigger matching',
            life: 5000,
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async triggerCleanup({ commit, dispatch }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await travelBuddyService.triggerCleanup();
        const data = response.data || response;

        const detail = data.message
          || (data.result
            ? `Scanned ${data.result.usersScanned} users, fixed ${data.result.usersFixed}, moved ${data.result.entriesMoved} entries${data.result.skippedMovedToEndTotal > 0 ? `, ${data.result.skippedMovedToEndTotal} user(s) moved to end (skipped earlier)` : ''}`
            : 'Cleanup completed successfully');

        dispatch(
          'notifications/showToast',
          {
            severity: 'success',
            summary: 'Cleanup Complete',
            detail,
            life: 5000,
          },
          { root: true },
        );

        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        dispatch(
          'notifications/showToast',
          {
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to run cleanup',
            life: 5000,
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchUsers({ commit }) {
      try {
        commit('SET_USERS_LOADING', true);
        const data = await usersService.fetchUsers();
        commit('SET_USERS', data);
        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_USERS_LOADING', false);
      }
    },

    async fetchUserTravelBuddyDetail(_, userId) {
      const response = await travelBuddyService.getHistoryByUserId(userId);
      return response.data || response;
    },

    async triggerMatchingForSelected({ dispatch }, userIds) {
      const response = await travelBuddyService.triggerMatchingForSelected(userIds);
      const data = response.data || response;

      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'Selective Matching Complete',
          detail: data.message || 'Matching completed',
          life: 5000,
        },
        { root: true },
      );

      return data;
    },

    async previewRemoveMatch(_, userIds) {
      const response = await travelBuddyService.previewRemoveMatch(userIds);
      return response.data || response;
    },

    async removeMatch(_, payload) {
      const {
        userIds,
        confirmPartialFeedback = false,
        forceDeleteCompleted = false,
        confirmForceDelete = false,
        forceDeleteReason,
      } = typeof payload === 'object' && payload !== null && !Array.isArray(payload)
        ? payload
        : { userIds: payload, confirmPartialFeedback: false };
      const response = await travelBuddyService.removeMatch(userIds, {
        confirmPartialFeedback,
        forceDeleteCompleted,
        confirmForceDelete,
        forceDeleteReason,
      });
      return response.data || response;
    },

    async previewSmartMatch(_, userIds) {
      const response = await travelBuddyService.previewSmartMatch(userIds);
      return response.data || response;
    },

    async confirmSmartMatch(_, pairs) {
      const response = await travelBuddyService.confirmSmartMatch(pairs);
      return response.data || response;
    },

    async scanAnomalies({ commit }) {
      try {
        commit('SET_ANOMALY_SCAN_LOADING', true);
        commit('SET_ANOMALY_SCAN', null);
        const response = await travelBuddyService.scanAnomalies();
        const data = response.data || response;
        commit('SET_ANOMALY_SCAN', data);
        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_ANOMALY_SCAN_LOADING', false);
      }
    },

    async fetchAnomalyUserDetails(_, userId) {
      const response = await travelBuddyService.getAnomalyUserDetails(userId);
      return response.data || response;
    },

    async repairAnomaly({ dispatch }, { repairType, userId, extraBody }) {
      const response = await travelBuddyService.repairAnomaly(repairType, userId, extraBody || {});
      const data = response.data || response;
      dispatch(
        'notifications/showToast',
        {
          severity: data.success ? 'success' : 'warn',
          summary: data.success ? 'Repair Applied' : 'Repair Skipped',
          detail: data.message || 'Repair completed',
          life: 5000,
        },
        { root: true },
      );
      return data;
    },

    async repairAllAnomalies({ dispatch }, userId) {
      const response = await travelBuddyService.repairAllAnomalies(userId);
      const data = response.data || response;
      const actionCount = (data.actions || []).length;
      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'All Repairs Applied',
          detail: `${actionCount} repair action(s) executed`,
          life: 5000,
        },
        { root: true },
      );
      return data;
    },

    async triggerCleanupForUser({ dispatch }, userId) {
      const response = await travelBuddyService.triggerCleanupForUser(userId);
      const data = response.data || response;

      dispatch(
        'notifications/showToast',
        {
          severity: 'success',
          summary: 'User Cleanup Complete',
          detail: data.message || `Cleanup done: ${data.result?.entriesMoved ?? 0} entries moved${(data.result?.skippedMovedToEnd ?? 0) > 0 ? `, ${data.result.skippedMovedToEnd} user(s) moved to end (skipped earlier)` : ''}`,
          life: 5000,
        },
        { root: true },
      );

      return data;
    },
  },

  getters: {
    isLoading: (state) => state.loading,
    hasError: (state) => !!state.error,
    getError: (state) => state.error,
  },
};
